import {useLayoutEffect, useRef, useState} from 'react'
import {publish, subscribe, unsubscribe} from '@/script/event.mjs'
import {createPlugin} from '../index.mjs'

export default () => {
    const refNodeToCount = useRef(new WeakMap)

    const extensions = () => ({
        getCount(node) {
            return refNodeToCount.current.get(node)
        },

        useCount(node) {
            const [count, setCount] = useState(
                this.descendantCount.getCount(node)
            )

            useLayoutEffect(
                () => {
                    if (! node) {
                        return
                    }

                    subscribe(node, 'descendant_count_change', setCount)

                    return () => {
                        unsubscribe(node, 'descendant_count_change', setCount)
                    }
                },

                [node]
            )

            return count
        },
    })

    const watchers = {
        init() {
            const count = node => {
                let c = 0

                for (const n of this.children(node)) {
                    c += count(n)
                }

                refNodeToCount.current.set(node, c)
                publish(node, 'descendant_count_change', c)
                return c + 1
            }

            count(this.root)
        },

        async model_change(modelChanges) {
            const updated = new Map

            const updateChain = (node, delta) => {
                for (const n of this.chain(node)) {
                    const c = refNodeToCount.current.get(n)

                    if (c && ! n.isDeleted) {
                        refNodeToCount.current.set(n, c + delta)
                        updated.set(n, c + delta)
                    }
                    else {
                        break
                    }
                }
            }

            for (const [node, changes] of modelChanges) {
                const c = refNodeToCount.current.get(node)

                if (changes.has('isDeleted')) {
                    refNodeToCount.current.delete(node)
                    updateChain(node.parent, - c - 1)
                }
                else if (changes.has('isCreated')) {
                    refNodeToCount.current.set(node, 0)
                    updated.set(node, 0)

                    for (const n of this.chain(node.parent)) {
                        const c = refNodeToCount.current.get(n) ?? 0
                        refNodeToCount.current.set(n, c + 1)
                        updated.set(n, c + 1)
                    }
                }
                else {
                    const {
                        newValue: newParent,
                        oldValue: oldParent,
                    } = changes.get('parent') ?? {}

                    if (oldParent) {
                        updateChain(oldParent, - c - 1)
                    }

                    if (newParent) {
                        updateChain(newParent, c + 1)
                    }
                }
            }

            for (const [node, count] of updated.entries()) {
                publish(node, 'descendant_count_change', count)
            }
        },
    }

    return createPlugin('descendantCount', {extensions, watchers})
}
